import * as THREE from 'three';
import './extensions/OrthographicCamera.js';
import './extensions/PerspectiveCamera.js';
import './extensions/Ray.js';
import unifiedWorkerPool from './utils/workers/UnifiedWorkerPool';
import { clearHierarchyLoadQueue } from './gemoetries/PPCPointCloudGeometry/PPCPointCloudGeometryNode';
import workerResponseQueue from './gemoetries/PPCPointCloudGeometry/WorkerResponseQueue';
import { PPCFileDb } from './loader/ppc/PPCFileDb';
import { PPCLoader as PPCLoaderV2 } from './loader/ppc/PPCLoader_v2';
import { PointCloudOctree } from './PointCloudOctree';
import { lru, resetRenderedTiles, resetLoadedTilesNetwork, resetStats, setNumNodesLoading, setFramenumber } from './potreeState';

// Export THREE so client code can access it via Potree.THREE
// This prevents duplicate THREE.js bundles
export { THREE };

// Patch THREE.TextureLoader to set crossOrigin for CORS support
// In ES modules, THREE is a frozen namespace, so we patch the prototype instead
const originalLoad = THREE.TextureLoader.prototype.load;
THREE.TextureLoader.prototype.load = function (...args: Parameters<typeof originalLoad>) {
  this.setCrossOrigin('anonymous');
  return originalLoad.apply(this, args);
};

export * from './Actions';
export * from './AnimationPath';
export * from './Annotation';
export * from './defines';
export * from './Enum';
export * from './EventDispatcher';
export * from './Features';
export * from './KeyCodes';
export * from './loader/PointAttributes';
export * from './LRU';
export * from './materials/ClassificationScheme';
export * from './materials/EyeDomeLightingMaterial';
export * from './materials/Gradients';
export * from './materials/NormalizationEDLMaterial';
export * from './materials/NormalizationMaterial';
export * from './materials/BlurMaterial';
export * from './materials/PointCloudMaterial';
export * from './modules/CameraAnimation/CameraAnimation';
export { DeviceOrientationControls } from './navigation/DeviceOrientationControls';
export { EarthControls } from './navigation/EarthControls';
export { FirstPersonControls } from './navigation/FirstPersonControls';
export { OrbitControls } from './navigation/OrbitControls';
export { VRControls } from './navigation/VRControls';
export * from './PointCloudOctree';
export * from './PointCloudOctreeGeometry';
export * from './PointCloudTree';
export * from './Points';
export * from './PotreeRenderer';
export * from './Potree_update_visibility';
export * from './TextSprite';
export * from './utils';
export * from './utils/Box3Helper';
export * from './utils/ClippingTool';
export * from './utils/Measure';
export * from './utils/Message';
export * from './utils/PointCloudSM';
export * from './utils/PointoramaAnnotation';
export * from './utils/CadObject';
export * from './utils/PolygonClipVolume';
export * from './utils/ScreenBoxSelectTool';
export * from './utils/SpotLightHelper';
export * from './utils/Volume';
export * from './utils/PointCluster';
export * from './Version';
export * from './viewer/Scene';
export * from './viewer/viewer';
export * from './utils/workers/UnifiedWorkerPool';
export * from './XHRFactory';
export * from './utils/PointoramaPlane';
export * from './utils/PointoramaRoom';
export * from './utils/PointoramaWallSketch';
export * from './utils/PointoramaSurface';

export const ppcFileDb = new PPCFileDb();

export const workerPool = unifiedWorkerPool;

let hasTouch: boolean | undefined;
if (window.matchMedia('(any-pointer: coarse)').matches) {
  hasTouch = true;
}

function checkIsTouchDevice() {
  return hasTouch;
}

export { stats, pointBudget, framenumber, numNodesLoading, maxNodesLoading, rendered_tiles, loaded_tiles_network, debug, scriptPath, resourcePath, _pointcloudTransformVersion } from './potreeState';

// Pythagoras - begin
export const isTouchDevice = checkIsTouchDevice();
// Pythagoras - end
export const version = {
  major: 1,
  minor: 8,
  suffix: '.0',
};

export { lru };

/**
 * Full cleanup of all Potree resources.
 * Call this when disposing the viewer or on page unload.
 */
export function cleanup() {
  // Clear worker pool
  unifiedWorkerPool.cleanup();
  clearHierarchyLoadQueue();
  workerResponseQueue.clear();

  // Clear IndexedDB cache
  ppcFileDb.clear();

  // Clear global tile tracking
  resetRenderedTiles();
  resetLoadedTilesNetwork();

  // Clear stats to prevent memory growth
  resetStats();

  // Clear global LRU cache (don't create new instance - just dispose and clear)
  // Creating a new LRU would break references from window.Potree.lru
  if (lru) {
    lru.dispose();
  }

  // Reset loading counters
  setNumNodesLoading(0);
  setFramenumber(0);
}

/**
 * Clear the global tile tracking objects.
 * Call this when removing point clouds to prevent memory leaks.
 */
export function clearTileTracking() {
  resetRenderedTiles();
  resetLoadedTilesNetwork();
}

// NOTE: We intentionally do NOT call cleanup() on beforeunload/pagehide.
// Calling cleanup during navigation can interrupt in-flight async operations
// and cause errors. The browser will handle memory cleanup on page unload.
// Call Potree.cleanup() explicitly when disposing the viewer during SPA navigation.

/** Event returned from point cloud loading */
interface PointCloudLoadEvent {
  type: string;
  pointcloud: PointCloudOctree;
}

export function loadPointCloud(path: string, name: string, callback?: (e: PointCloudLoadEvent) => void, _isOld?: boolean, id?: string, visible?: boolean) {
  const loaded = function (e: PointCloudLoadEvent) {
    e.pointcloud.name = name;
    if (callback) callback(e);
  };

  const promise = new Promise<PointCloudLoadEvent>((resolve) => {
    if (!path) {
      console.error(new Error('No path provided for point cloud loading'));
    } else if (path.indexOf('.ppch') > 0) {
      PPCLoaderV2.load(path, name).then((headerData) => {
        if (!headerData) {
          console.error(new Error(`Failed to load point cloud from URL: ${path}`));
        } else {
          // @ts-expect-error - PPCPointCloudGeometry is structurally compatible with PCOGeometry at runtime
          const pointcloud = new PointCloudOctree(headerData.ppcGeometry, undefined);
          pointcloud.identifier = id;
          pointcloud.visible = visible !== undefined ? visible : true;
          pointcloud.segmentList = headerData.segmentList || [];
          resolve({ type: 'pointcloud_loaded', pointcloud: pointcloud });
        }
      });
    } else {
      console.error(new Error(`Unsupported point cloud format: ${path}`));
    }
  });

  if (callback) {
    promise.then((pointcloud) => {
      loaded(pointcloud);
    });
  } else {
    return promise;
  }
}

/** jQuery type declarations for the selectgroup plugin */
declare const jQuery: JQueryStatic | undefined;
interface JQueryStatic {
  (selector: string | HTMLElement): JQueryObject;
  fn: { extend: (obj: Record<string, unknown>) => void };
}
interface JQueryObject {
  prop: (name: string, value?: string) => string & JQueryObject;
  html: () => string;
  find: (selector: string) => JQueryObject;
  each: (callback: (index: number, value: HTMLElement) => void) => JQueryObject;
  change: (handler: () => void) => JQueryObject;
  is: (selector: string) => boolean;
  addClass: (className: string) => JQueryObject;
  removeClass: (className: string) => JQueryObject;
  css: (prop: string, value: string) => JQueryObject;
  append: (el: JQueryObject) => JQueryObject;
  empty: () => JQueryObject;
  push?: (el: JQueryObject) => void;
}

// add selectgroup (jQuery plugin - only runs after jQuery is loaded via script tag)
if (typeof jQuery !== 'undefined')
  (function ($: JQueryStatic) {
    $.fn.extend({
      selectgroup: function (this: JQueryObject, args: { title?: string } = {}) {
        const elGroup = $(this as unknown as HTMLElement);
        const rootID = elGroup.prop('id');
        const groupID = `${rootID}`;
        const groupTitle = args.title !== undefined ? args.title : '';

        const elButtons: JQueryObject[] = [];
        elGroup.find('option').each((_index: number, value: HTMLElement) => {
          const buttonID = $(value).prop('id');
          const label = $(value).html();
          const optionValue = $(value).prop('value');

          const elButton = $(`
					<span style="flex-grow: 1; display: inherit">
					<label for="${buttonID}" class="ui-button" style="width: 100%; padding: .4em .1em">${label}</label>
					<input type="radio" name="${groupID}" id="${buttonID}" value="${optionValue}" style="display: none"/>
					</span>
				`);
          const elLabel = elButton.find('label');
          const elInput = elButton.find('input');

          elInput.change(() => {
            elGroup.find('label').removeClass('ui-state-active');
            elGroup.find('label').addClass('ui-state-default');
            if (elInput.is(':checked')) {
              elLabel.addClass('ui-state-active');
            } else {
              //elLabel.addClass("ui-state-default");
            }
          });

          elButtons.push(elButton);
        });

        const elFieldset = $(`
				<fieldset style="border: none; margin: 0px; padding: 0px">
					<legend>${groupTitle}</legend>
					<span style="display: flex">

					</span>
				</fieldset>
			`);

        const elButtonContainer = elFieldset.find('span');
        for (const elButton of elButtons) {
          elButtonContainer.append(elButton);
        }

        elButtonContainer.find('label').each((_index: number, value: HTMLElement) => {
          $(value).css('margin', '0px');
          $(value).css('border-radius', '0px');
          $(value).css('border', '1px solid black');
          $(value).css('border-left', 'none');
        });
        elButtonContainer.find('label:first').each((_index: number, value: HTMLElement) => {
          $(value).css('border-radius', '4px 0px 0px 4px');
        });
        elButtonContainer.find('label:last').each((_index: number, value: HTMLElement) => {
          $(value).css('border-radius', '0px 4px 4px 0px');
          $(value).css('border-left', 'none');
        });

        elGroup.empty();
        elGroup.append(elFieldset);
      },
    });
  })(jQuery);
